import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import SearchIcon from '@mui/icons-material/Search'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import AtualizarIcon from '@mui/icons-material/Refresh'
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined'
import RecolherIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import ExpandirIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import { PowerBIEmbed } from 'powerbi-client-react'
import services from '../../services'
import RelatorioHtmlViewer from '../../components/RelatorioHtmlViewer'
import OneflowNovo from '../oneflow/OneflowNovo'
import { useNotification } from '../../hooks/useNotification.js'
import { useAuth } from '../../hooks/useAuth'
import { alternarFavorito, AmbienteNaArvore, carregarArvore, DashboardNaArvore, ehFavorito, GrupoNaArvore, useNovoPortal } from '../estado'
import { marcaDoAmbiente } from '../tema'
import './dashboard.scss'

interface SuaVisao {
  administrador: boolean
  nomeDaRegra?: string | null
  colunaDaRegra?: string | null
  valoresDaRegra: string[]
  semValorNaRegra: boolean
  /** Sem valor na regra, mas a regra libera quem não tem: abre sem o recorte dela. */
  veTudo?: boolean
  filtros?: string | null
  atualizacao: 'ok' | 'falhou' | 'atualizando' | 'direto' | 'sem-historico'
  atualizadoEm?: string | null
  /** Só chega para quem administra o BI. */
  falhouEm?: string | null
}

interface Token { enbedUrl: string; token: string; expiration: string; tokenId: string }

function lembrar(chave: string, valor: boolean) { try { localStorage.setItem(chave, valor ? '1' : '0') } catch { /* sem armazenamento */ } }
function lembrado(chave: string) { try { return localStorage.getItem(chave) === '1' } catch { return false } }

function quando(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso), hoje = new Date(), ontem = new Date(Date.now() - 86400000)
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  if (d.toDateString() === hoje.toDateString()) return `hoje, ${hora}`
  if (d.toDateString() === ontem.toDateString()) return `ontem, ${hora}`
  return `${d.toLocaleDateString('pt-BR')}, ${hora}`
}

function textoAtualizacao(v: SuaVisao): string {
  if (v.atualizacao === 'falhou') return `A última atualização falhou${v.falhouEm ? ` (${quando(v.falhouEm)})` : ''}.${v.atualizadoEm ? ` Os números são de ${quando(v.atualizadoEm)}.` : ''} Só administradores do BI veem este aviso.`
  if (v.atualizacao === 'atualizando') return `Os dados estão sendo atualizados agora.${v.atualizadoEm ? ` Os números na tela são de ${quando(v.atualizadoEm)}.` : ''}`
  if (v.atualizacao === 'ok' && v.atualizadoEm) return `Dados atualizados ${quando(v.atualizadoEm)}.`
  if (v.atualizacao === 'direto') return 'Este dashboard consulta os dados direto da fonte.'
  return 'Ainda não há registro de atualização deste dashboard.'
}

/** Ao lado do título: de quando é o dado na tela. */
function seloAtualizacao(v: SuaVisao | null): string | null {
  if (!v) return null
  if (v.atualizacao === 'direto') return 'Dados direto da fonte'
  return v.atualizadoEm ? `Atualizado ${quando(v.atualizadoEm)}` : null
}

/** Seletor de ambiente no topo: lista com busca, para quem tem dezenas de ambientes. */
function SeletorAmbiente({ arvore, atual, aoEscolher }: { arvore: AmbienteNaArvore[]; atual?: AmbienteNaArvore; aoEscolher: (a: AmbienteNaArvore) => void }) {
  const est = useNovoPortal()
  const [aberto, setAberto] = useState(false)
  const [busca, setBusca] = useState('')
  const caixa = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!aberto) return
    const fora = (e: MouseEvent) => { if (!caixa.current?.contains(e.target as Node)) setAberto(false) }
    document.addEventListener('mousedown', fora)
    return () => document.removeEventListener('mousedown', fora)
  }, [aberto])
  const q = busca.trim().toLowerCase()
  const lista = arvore.filter(a => !q || a.nome.toLowerCase().includes(q))
  const marca = atual ? marcaDoAmbiente(est.marcas, est.ambientes, atual.id) : null
  return (
    <div className="nd-ambiente" ref={caixa}>
      <button type="button" className="nd-ambiente-botao" onClick={() => { setAberto(!aberto); setBusca('') }} aria-expanded={aberto} aria-haspopup="listbox">
        <span className="nd-ambiente-ini">{(atual?.nome || '?').charAt(0)}</span>
        <span className="nd-ambiente-nome"><small>Ambiente</small><b>{atual?.nome || 'Escolha o ambiente'}</b></span>
        {marca && <span className="nd-tag">{marca.nome}</span>}
        <ExpandMoreIcon fontSize="small" />
      </button>
      {aberto && (
        <div className="nd-ambiente-lista" role="listbox">
          {arvore.length > 6 && (
            <label className="nd-filtro"><SearchIcon fontSize="small" />
              <input autoFocus value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar ambiente" aria-label="Buscar ambiente" />
            </label>
          )}
          {lista.map(a => (
            <button key={a.id} type="button" role="option" aria-selected={a.id === atual?.id} className="nd-ambiente-item"
                    onClick={() => { setAberto(false); aoEscolher(a) }}>
              <span>{a.nome}</span>
              <span className="nd-tag">{marcaDoAmbiente(est.marcas, est.ambientes, a.id).nome}</span>
            </button>
          ))}
          {lista.length === 0 && <p className="nd-vazio">Nenhum ambiente com esse nome.</p>}
        </div>
      )}
    </div>
  )
}

export default function NovoDashboard() {
  const { id, envId } = useParams()
  const navigate = useNavigate()
  const { notify } = useNotification()
  const { getUserRoles } = useAuth()
  const podeAtualizar = ((getUserRoles() || []) as string[]).includes('bi_datasets_refresh')
  const gestor = ((getUserRoles() || []) as string[]).includes('showmenu_bi_dashboards_edit')
  // Menu do botão direito num dashboard da lista (ou no título).
  const [menuCtx, setMenuCtx] = useState<{ x: number; y: number; d: DashboardNaArvore } | null>(null)
  useEffect(() => {
    if (!menuCtx) return
    const fechar = () => setMenuCtx(null)
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') fechar() }
    window.addEventListener('click', fechar)
    window.addEventListener('scroll', fechar, true)
    window.addEventListener('keydown', tecla)
    return () => { window.removeEventListener('click', fechar); window.removeEventListener('scroll', fechar, true); window.removeEventListener('keydown', tecla) }
  }, [menuCtx])
  const abrirMenu = (e: React.MouseEvent, d: DashboardNaArvore) => {
    e.preventDefault()
    setMenuCtx({ x: Math.min(e.clientX, window.innerWidth - 250), y: Math.min(e.clientY, window.innerHeight - 200), d })
  }
  const [pedindo, setPedindo] = useState(false)
  const est = useNovoPortal()
  const [semLista, setSemLista] = useState(lembrado('nd-sem-lista'))
  const [listaCelular, setListaCelular] = useState(false)
  const [buscaLista, setBuscaLista] = useState('')
  // Grupos abertos ou fechados na lista, escolha da pessoa (por ambiente e grupo).
  const [abertos, setAbertos] = useState<Record<string, boolean>>({})
  const [token, setToken] = useState<Token | null>(null)
  const [erroToken, setErroToken] = useState<string | null>(null)
  const [visao, setVisao] = useState<SuaVisao | null>(null)
  const [visaoAberta, setVisaoAberta] = useState(false)
  const [recarga, setRecarga] = useState(0)
  const palco = useRef<HTMLDivElement>(null)
  // Tela cheia simulada, para onde o navegador não deixa (iPhone): o palco cobre a tela inteira por CSS.
  const [cheia, setCheia] = useState(false)

  useEffect(() => { carregarArvore() }, [])

  const arvore = est.arvore || []
  const ambienteId = Number(envId) || null
  const dashboardId = Number(id) || null
  const ambiente = arvore.find(a => a.id === ambienteId)

  // Sem ambiente ou dashboard na URL (ou um que a pessoa não abre mais): vai para o primeiro favorito, senão o primeiro dashboard.
  useEffect(() => {
    if (!est.arvore) return
    const valido = ambiente?.grupos.some(g => g.dashboards.some(d => d.id === dashboardId))
    if (valido) return
    const alvoAmb = ambiente || est.arvore[0]
    if (!alvoAmb) return
    const fav = est.favoritos.find(f => f.ambienteId === alvoAmb.id && alvoAmb.grupos.some(g => g.dashboards.some(d => d.id === f.dashboardId)))
    const primeiro = alvoAmb.grupos.find(g => g.dashboards.length)?.dashboards[0]
    const destino = fav?.dashboardId || primeiro?.id
    if (destino) navigate(`/app/dashboard/${destino}/${alvoAmb.id}`, { replace: true })
  }, [est.arvore, est.favoritos, ambiente, dashboardId, navigate])

  let grupoAtual: GrupoNaArvore | undefined
  let dashAtual: DashboardNaArvore | undefined
  ambiente?.grupos.forEach(g => g.dashboards.forEach(d => { if (d.id === dashboardId) { grupoAtual = g; dashAtual = d } }))

  // Token do Power BI; o relatório HTML abre por outro caminho, com os filtros aplicados no servidor.
  const pedirToken = useCallback(async () => {
    if (!dashboardId || !ambienteId) return
    const res = await services.api.powerbiKey(dashboardId, ambienteId)
    if (res.fail) {
      setErroToken(res.status === 403 ? 'Você não tem permissão para abrir este dashboard.' : (res.error || 'Não foi possível abrir o Power BI agora.'))
      return
    }
    setErroToken(null)
    setToken(res.data)
  }, [dashboardId, ambienteId])

  useEffect(() => {
    setToken(null); setErroToken(null); setVisao(null)
    if (!dashAtual) return
    if (dashAtual.tipo === 'powerbi') pedirToken()
    services.api.novoPortalSuaVisao(dashAtual.id, ambienteId).then((r: any) => { if (!r.fail) setVisao(r.data) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashAtual?.id, ambienteId])

  // "Atualizar a tela sozinho": renova o token e redesenha no intervalo do cadastro.
  useEffect(() => {
    if (!dashAtual?.atualizarACada || dashAtual.tipo !== 'powerbi') return
    const t = setInterval(() => { pedirToken(); setRecarga(r => r + 1) }, dashAtual.atualizarACada * 1000)
    return () => clearInterval(t)
  }, [dashAtual, pedirToken])

  const favoritosDoAmbiente = useMemo(() => {
    if (!ambiente) return []
    const todos = ambiente.grupos.flatMap(g => g.dashboards)
    return est.favoritos.filter(f => f.ambienteId === ambiente.id)
      .map(f => todos.find(d => d.id === f.dashboardId)).filter(Boolean) as DashboardNaArvore[]
  }, [ambiente, est.favoritos])

  const abrir = (d: DashboardNaArvore) => { setListaCelular(false); navigate(`/app/dashboard/${d.id}/${ambienteId}`) }
  const trocarAmbiente = (a: AmbienteNaArvore) => navigate(`/app/dashboard/0/${a.id}`)
  const estrela = async (d: DashboardNaArvore) => {
    if (!ambienteId) return
    const erro = await alternarFavorito(d.id, ambienteId)
    if (erro) notify(erro, { variant: 'error' })
  }
  // "Atualizar dados": pede o refresh do dataset ao Power BI; a situação aparece no "Sua visão".
  const atualizarDados = async () => {
    if (!dashAtual || !ambienteId) return
    setPedindo(true)
    const r = await services.api.novoPortalAtualizarDados(dashAtual.id, ambienteId)
    setPedindo(false)
    if (r.fail) { notify(r.error || 'Não foi possível pedir a atualização.', { variant: 'error' }); return }
    notify('Atualização pedida ao Power BI. Costuma levar alguns minutos; depois, recarregue o dashboard.', { variant: 'success' })
    services.api.novoPortalSuaVisao(dashAtual.id, ambienteId).then((v: any) => { if (!v.fail) setVisao(v.data) })
  }
  // Tela cheia de verdade onde o navegador oferece; no iPhone o Safari só faz isso com vídeo, então o palco passa a cobrir a tela por CSS.
  const telaCheia = () => {
    const el = palco.current
    if (el && document.fullscreenEnabled && typeof el.requestFullscreen === 'function') {
      el.requestFullscreen().catch(() => setCheia(true))
    } else {
      setCheia(true)
    }
  }
  const sairDaCheia = () => { setCheia(false); if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {}) }

  // Na tela cheia simulada o fundo não rola, Esc sai, e trocar de dashboard também sai.
  useEffect(() => {
    if (!cheia) return
    document.body.classList.add('nd-sem-rolagem')
    const teclas = (e: KeyboardEvent) => { if (e.key === 'Escape') setCheia(false) }
    window.addEventListener('keydown', teclas)
    return () => { document.body.classList.remove('nd-sem-rolagem'); window.removeEventListener('keydown', teclas) }
  }, [cheia])
  useEffect(() => { setCheia(false) }, [dashboardId, ambienteId])
  const alternarLista = () => { lembrar('nd-sem-lista', !semLista); setSemLista(!semLista) }

  const q = buscaLista.trim().toLowerCase()
  const itemLista = (d: DashboardNaArvore, chave: string) => {
    const fav = !!ambienteId && ehFavorito(est, d.id, ambienteId)
    return (
      <div key={chave} className="nd-item" aria-current={d.id === dashboardId ? 'page' : undefined} onContextMenu={e => abrirMenu(e, d)}>
        <button type="button" className="nd-item-nome" onClick={() => abrir(d)}>{d.nome}{d.tipo === 'html' && <span className="nd-tipo">HTML</span>}</button>
        <button type="button" className={`nd-estrela${fav ? ' on' : ''}`} aria-pressed={fav} onClick={() => estrela(d)}
                aria-label={fav ? `Tirar ${d.nome} dos favoritos` : `Marcar ${d.nome} como favorito`} title={fav ? 'Tirar dos favoritos' : 'Marcar como favorito'}>
          {fav ? <StarIcon fontSize="inherit" /> : <StarBorderIcon fontSize="inherit" />}
        </button>
      </div>
    )
  }

  // Com poucos grupos, todos abertos; com muitos, só o do dashboard aberto (e os Favoritos). A busca abre todos.
  const grupoDaLista = (id: string, nome: string, ds: DashboardNaArvore[], temAtual: boolean) => {
    const chave = `${ambienteId}:${id}`
    const aberto = !!q || (abertos[chave] ?? ((ambiente?.grupos.length || 0) <= 4 || temAtual))
    return (
      <div key={chave} className={`nd-grupo${aberto ? ' aberto' : ''}`}>
        <button type="button" className="nd-grupo-nome" aria-expanded={aberto} onClick={() => setAbertos(a => ({ ...a, [chave]: !aberto }))}>
          <ExpandMoreIcon fontSize="small" className="nd-grupo-seta" />
          <span>{nome}</span>
          <small>{ds.length}</small>
        </button>
        {aberto && <div className="nd-grupo-itens">{ds.map(d => itemLista(d, `${id}-${d.id}`))}</div>}
      </div>
    )
  }

  if (est.erroArvore) return <div className="nd-mensagem">{est.erroArvore}</div>
  if (est.arvore && est.arvore.length === 0) return <div className="nd-mensagem">Você ainda não tem acesso a nenhum dashboard. Fale com o administrador do BI.</div>

  const favAtual = !!dashAtual && !!ambienteId && ehFavorito(est, dashAtual.id, ambienteId)

  return (
    <OneflowNovo dashId={dashAtual?.id} ambienteId={ambienteId} dashNome={dashAtual?.nome} ligado={!!dashAtual?.oneflow}>
      <div className={`nd${semLista ? ' sem-lista' : ''}${listaCelular ? ' lista-celular' : ''}`}>
        <header className="nd-topo">
          <SeletorAmbiente arvore={arvore} atual={ambiente} aoEscolher={trocarAmbiente} />
          <button type="button" className="nd-buscar" onClick={() => window.dispatchEvent(new Event('np-abrir-busca'))}>
            <SearchIcon fontSize="small" /><span>Buscar dashboard</span><kbd>Ctrl K</kbd>
          </button>
        </header>

        <div className="nd-corpo">
          <aside className="nd-lista" aria-label="Dashboards do ambiente">
            <div className="nd-lista-cab">
              <b>Dashboards</b>
              <button type="button" className="nd-ico" onClick={() => (listaCelular ? setListaCelular(false) : alternarLista())} aria-label="Recolher a lista" title="Recolher a lista"><RecolherIcon fontSize="small" /></button>
            </div>
            <label className="nd-filtro"><SearchIcon fontSize="small" />
              <input value={buscaLista} onChange={e => setBuscaLista(e.target.value)} placeholder="Buscar neste ambiente" aria-label="Buscar dashboard neste ambiente" />
            </label>
            {!q && favoritosDoAmbiente.length > 0 && grupoDaLista('fav', 'Favoritos', favoritosDoAmbiente, true)}
            {ambiente?.grupos.map(g => {
              const ds = g.dashboards.filter(d => !q || d.nome.toLowerCase().includes(q) || g.nome.toLowerCase().includes(q))
              return ds.length ? grupoDaLista(String(g.id), g.nome, ds, g === grupoAtual) : null
            })}
            {!est.arvore && <p className="nd-vazio">Carregando…</p>}
          </aside>

          <section className="nd-dash" aria-label={dashAtual?.nome || 'Dashboard'}>
            <div className="nd-dash-cab">
              <button type="button" className="nd-ico nd-abrir-lista" onClick={() => (window.innerWidth <= 768 ? setListaCelular(true) : alternarLista())}
                      aria-label="Mostrar a lista de dashboards" title="Mostrar a lista de dashboards"><ExpandirIcon fontSize="small" /></button>
              <h1 onContextMenu={e => dashAtual && abrirMenu(e, dashAtual)}>{dashAtual?.nome || ' '}</h1>
              {dashAtual && (
                <button type="button" className={`nd-estrela grande${favAtual ? ' on' : ''}`} aria-pressed={favAtual} onClick={() => estrela(dashAtual!)}
                        aria-label={favAtual ? 'Tirar dos favoritos' : 'Marcar como favorito'} title={favAtual ? 'Tirar dos favoritos' : 'Marcar como favorito'}>
                  {favAtual ? <StarIcon fontSize="inherit" /> : <StarBorderIcon fontSize="inherit" />}
                </button>
              )}
              {seloAtualizacao(visao) && <span className="nd-selo" title="De quando é o dado na tela">{seloAtualizacao(visao)}</span>}
              <span className="nd-espaco" />
              {visao && (
                <button type="button" className={`nd-acao${visao.semValorNaRegra || visao.atualizacao === 'falhou' ? ' alerta' : ''}`} aria-expanded={visaoAberta} onClick={() => setVisaoAberta(!visaoAberta)}>
                  <VisibilityIcon fontSize="small" /><span>Sua visão</span>
                </button>
              )}
              {podeAtualizar && dashAtual && (
                <button type="button" className="nd-acao" disabled={pedindo || visao?.atualizacao === 'atualizando' || visao?.atualizacao === 'direto'} onClick={atualizarDados}
                        title={visao?.atualizacao === 'direto' ? 'DirectQuery: o dado já vem direto da fonte' : visao?.atualizacao === 'atualizando' ? 'Já está atualizando' : 'Pedir ao Power BI para atualizar o dataset agora'}>
                  <AtualizarIcon fontSize="small" /><span>{pedindo ? 'Pedindo…' : visao?.atualizacao === 'atualizando' ? 'Atualizando…' : 'Atualizar dados'}</span>
                </button>
              )}
              <button type="button" className="nd-acao" onClick={telaCheia}><FullscreenIcon fontSize="small" /><span>Tela cheia</span></button>
            </div>

            {visao && visaoAberta && (
              <div className="nd-visao">
                <div><b>Atualização</b><p className={visao.atualizacao === 'falhou' ? 'ruim' : ''}>{textoAtualizacao(visao)}</p></div>
                <div><b>Seu recorte</b>
                  {visao.administrador
                    ? <p>Você administra o BI: vê todos os dados, sem regra.</p>
                    : visao.semValorNaRegra
                      ? <p className="ruim">Você não tem valor na regra “{visao.nomeDaRegra}”, por isso este dashboard abre vazio. Fale com o administrador do BI.</p>
                      : visao.veTudo
                        ? <p>Você não tem valor na regra “{visao.nomeDaRegra}”, e ela libera quem não tem: você vê todos os dados.</p>
                      : visao.valoresDaRegra.length
                        ? <p>Pela regra “{visao.nomeDaRegra}”, você vê só <code>{visao.colunaDaRegra}</code> em {visao.valoresDaRegra.map(v => <span key={v} className="nd-valor">{v}</span>)}</p>
                        : <p>Sem regra: todos que abrem veem o mesmo recorte.</p>}
                </div>
                <div><b>Filtros do ambiente</b><p>{visao.filtros ? `Vale só onde ${visao.filtros}.` : 'Nenhum filtro neste ambiente.'}</p></div>
              </div>
            )}

            <div className={`nd-palco${cheia ? ' cheia' : ''}`} ref={palco}>
              {cheia && (
                <button type="button" className="nd-sair-cheia" onClick={sairDaCheia} aria-label="Sair da tela cheia" title="Sair da tela cheia"><FullscreenExitIcon fontSize="small" /></button>
              )}
              {dashAtual?.tipo === 'html' && ambienteId && <RelatorioHtmlViewer dashboardId={dashAtual.id} ambienteId={ambienteId} />}
              {dashAtual?.tipo === 'powerbi' && erroToken && <div className="nd-mensagem">{erroToken}</div>}
              {dashAtual?.tipo === 'powerbi' && token?.enbedUrl && (
                <PowerBIEmbed
                  key={`${dashAtual.id}-${recarga}`}
                  embedConfig={{
                    type: 'report', id: undefined, embedUrl: token.enbedUrl, accessToken: token.token, tokenType: 1,
                    settings: { filterPaneEnabled: dashAtual.painelDeFiltros, navContentPaneEnabled: dashAtual.navegacao }
                  }}
                  cssClassName="nd-embed"
                />
              )}
              {dashAtual?.tipo === 'powerbi' && !token && !erroToken && <div className="nd-mensagem">Abrindo o dashboard…</div>}
            </div>
          </section>
        </div>
        <div className="nd-veu" onClick={() => setListaCelular(false)} aria-hidden="true" />
        {menuCtx && ambienteId && (
          <div className="nd-menu-ctx" role="menu" style={{ left: menuCtx.x, top: menuCtx.y }} onClick={e => e.stopPropagation()}>
            <div className="nd-menu-ctx-titulo">{menuCtx.d.nome}</div>
            <button type="button" role="menuitem" onClick={() => { setMenuCtx(null); abrir(menuCtx.d) }}>Abrir</button>
            <button type="button" role="menuitem" onClick={() => { setMenuCtx(null); window.open(`/app/dashboard/${menuCtx.d.id}/${ambienteId}`, '_blank', 'noopener') }}>Abrir em nova aba</button>
            <button type="button" role="menuitem" onClick={() => { setMenuCtx(null); estrela(menuCtx.d) }}>{ehFavorito(est, menuCtx.d.id, ambienteId) ? 'Tirar dos favoritos' : 'Marcar como favorito'}</button>
            {gestor && <hr />}
            {gestor && <button type="button" role="menuitem" onClick={() => { setMenuCtx(null); navigate(`/app/gestao/dashboards/${menuCtx.d.id}/ambiente/${ambienteId}`) }}>Configurar neste ambiente</button>}
            {gestor && <button type="button" role="menuitem" onClick={() => { setMenuCtx(null); navigate(`/app/gestao/dashboards/${menuCtx.d.id}`) }}>Configurações gerais</button>}
          </div>
        )}
      </div>
    </OneflowNovo>
  )
}
